import React, { useState } from "react";
import { Logo } from "@/components/Logo";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Lock, User, Users, Building2, AlertCircle, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/format";

const Register = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [role, setRole] = useState(params.get("role") === "klant" ? "klant" : "kantoor");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError(t("register.min_chars")); return; }
    if (password !== confirm) { setError(t("register.no_match")); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/register", { email, password, name, role });
      setUser(res.data.user);
      navigate(res.data.user.role === "klant" ? "/mijn-matches" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex mb-8" data-testid="register-logo-link"><Logo size="md" /></Link>
        <Link to="/login" className="text-sm text-slate-500 hover:text-[#1A2B4A] inline-flex items-center gap-1 mb-3" data-testid="back-to-login">
          <ArrowLeft size={14} /> {t("register.back_to_login")}
        </Link>

        <div className="beecenter-card p-8">
          <h1 className="font-heading text-3xl font-extrabold text-[#1A2B4A]">{t("register.title")}</h1>
          <p className="text-slate-600 mt-1 text-sm">{t("register.subtitle")}</p>

          <div className="mt-5 grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-2xl" data-testid="register-role-tabs">
            <button onClick={() => setRole("klant")} type="button"
              className={`py-2.5 px-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                role === "klant" ? "bg-white shadow-sm text-[#1A2B4A]" : "text-slate-500"
              }`} data-testid="register-tab-klant">
              <Users size={14} /> {t("register.tab_klant")}
            </button>
            <button onClick={() => setRole("kantoor")} type="button"
              className={`py-2.5 px-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                role === "kantoor" ? "bg-white shadow-sm text-[#1A2B4A]" : "text-slate-500"
              }`} data-testid="register-tab-kantoor">
              <Building2 size={14} /> {t("register.tab_kantoor")}
            </button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <Field icon={User} label={role === "kantoor" ? t("register.name_kantoor") : t("register.name_klant")} value={name} onChange={setName} placeholder={role === "kantoor" ? "MY belastingadvies" : "Sander de Vries"} testid="register-name" />
            <Field icon={Mail} label={t("register.email")} value={email} onChange={setEmail} type="email" placeholder="you@email.com" testid="register-email" />
            <Field icon={Lock} label={t("register.password")} value={password} onChange={setPassword} type="password" placeholder="••••••••" testid="register-password" />
            <Field icon={Lock} label={t("register.confirm")} value={confirm} onChange={setConfirm} type="password" placeholder="••••••••" testid="register-confirm" />
            {error && (
              <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2 text-sm" data-testid="register-error">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <button type="submit" disabled={loading || !email || !password || !name} className="btn-amber w-full !py-3 disabled:opacity-50" data-testid="btn-register">
              {loading ? t("common.loading") : t("register.submit")}
            </button>
          </form>
          <p className="text-xs text-slate-500 mt-4 text-center">
            {t("register.terms")} <a href="#" className="underline">{t("register.voorwaarden")}</a> {t("register.terms").endsWith("our") || t("register.terms").endsWith(" and") ? "&" : "en"} <a href="#" className="underline">{t("register.privacybeleid")}</a>.
          </p>
        </div>

        <p className="text-sm text-slate-600 text-center mt-6">
          {t("register.have_account")} <Link to="/login" className="font-semibold text-[#F5A623] hover:underline">{t("register.login_link")}</Link>
        </p>
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, value, onChange, type = "text", placeholder, testid }) => (
  <div>
    <label className="text-xs font-semibold text-[#1A2B4A] uppercase tracking-wider">{label}</label>
    <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 focus-within:border-[#F5A623] focus-within:ring-2 focus-within:ring-[#F5A623]/20 transition-colors">
      <Icon size={16} className="text-slate-400" />
      <input type={type} required value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} className="w-full bg-transparent outline-none text-sm" data-testid={testid} />
    </div>
  </div>
);

export default Register;
